/**
 * Framework-agnostic Sendbird client for messaging.
 * Uses Sendbird SDK v4 and stays UI-framework agnostic.
 */

import SendbirdChat from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import type { GroupChannelHandler } from '@sendbird/chat/groupChannel';

export interface MessagesClientConfig {
  applicationId: string;
  userId: string;
  accessToken?: string;
}

export interface MessagesClient {
  connect(): Promise<void>;
  disconnect(): void;
  addGroupChannelHandler(handlerId: string, handler: GroupChannelHandler): void;
  removeGroupChannelHandler(handlerId: string): void;
  getSdk(): ReturnType<typeof SendbirdChat.init>;
}

/**
 * Creates a messaging client instance.
 */
export function createMessagesClient(config: MessagesClientConfig): MessagesClient {
  let client: ReturnType<typeof SendbirdChat.init> | null = null;

  const initializeClient = (): ReturnType<typeof SendbirdChat.init> => {
    if (!client) {
      client = SendbirdChat.init({
        appId: config.applicationId,
        modules: [new GroupChannelModule()],
      }) as unknown as ReturnType<typeof SendbirdChat.init>;
    }

    return client;
  };

  return {
    async connect(): Promise<void> {
      const sb = initializeClient();
      await sb.connect(config.userId, config.accessToken ?? undefined);
    },
    disconnect(): void {
      if (client) {
        client.disconnect();
        client = null;
      }
    },
    addGroupChannelHandler(handlerId: string, handler: GroupChannelHandler): void {
      const sb = initializeClient() as any;
      sb.groupChannel.addGroupChannelHandler(handlerId, handler);
    },
    removeGroupChannelHandler(handlerId: string): void {
      if (client) {
        (client as any).groupChannel.removeGroupChannelHandler(handlerId);
      }
    },
    getSdk(): ReturnType<typeof SendbirdChat.init> {
      return initializeClient();
    },
  };
}
